import {IShippingOption} from '../../i';

export const SHIP_SELECT_CHECKOUT_KEY = 'cc-ship-select-no-change';

export type ShippingOptionPrices = {
    shipping: number;
    production: number;
};

export type ShippingOptionFlight = {
    pending: boolean;
    queued: IShippingOption | undefined;
    inFlight: IShippingOption | undefined;
};

export const createShippingOptionFlight = (): ShippingOptionFlight => ({
    pending: false,
    queued: undefined,
    inFlight: undefined,
});

export type ShippingOptionChangeDeps = {
    isLocked?: () => boolean;
    flight: ShippingOptionFlight;
    setBusy: (busy: boolean) => void;
    getSelected: () => IShippingOption | undefined;
    getPrices: () => ShippingOptionPrices;
    setSelected: (method: IShippingOption | undefined, prices: ShippingOptionPrices) => void;
    setQueued: (option: IShippingOption | undefined) => void;
    batch: (fn: () => void) => void;
    checkout: (key: string) => Promise<boolean>;
    identify: () => void | Promise<void>;
};

const cloneShippingOption = (option: IShippingOption): IShippingOption => JSON.parse(JSON.stringify(option)) as IShippingOption;

const isSameMethod = (left: IShippingOption | undefined, right: IShippingOption | undefined): boolean => left != undefined && right != undefined && left.key === right.key;

export async function applyShippingOptionChange(option: IShippingOption, deps: ShippingOptionChangeDeps): Promise<void> {
    if (deps.isLocked?.()) {
        return;
    }

    if (deps.flight.pending) {
        if (isSameMethod(deps.flight.inFlight, option)) {
            deps.flight.queued = undefined;
            deps.batch(() => {
                deps.setQueued(undefined);
            });
            return;
        }
        const queued = cloneShippingOption(option);
        deps.flight.queued = queued;
        deps.batch(() => {
            deps.setQueued(queued);
        });
        return;
    }

    if (isSameMethod(deps.getSelected(), option)) {
        return;
    }

    deps.flight.pending = true;
    deps.flight.queued = undefined;
    deps.flight.inFlight = cloneShippingOption(option);
    let nextOption: IShippingOption | undefined = cloneShippingOption(option);

    try {
        while (nextOption != undefined) {
            const current = deps.getSelected();
            if (isSameMethod(current, nextOption)) {
                break;
            }

            const previousMethod = current != undefined ? cloneShippingOption(current) : undefined;
            const previousPrices = deps.getPrices();
            const nextPrices: ShippingOptionPrices = {
                shipping: nextOption.price_shipping,
                production: nextOption.price_production,
            };

            deps.flight.inFlight = cloneShippingOption(nextOption);
            deps.batch(() => {
                deps.setBusy(true);
                deps.setQueued(undefined);
                deps.setSelected(cloneShippingOption(nextOption as IShippingOption), nextPrices);
            });

            let success = false;
            try {
                success = await deps.checkout(SHIP_SELECT_CHECKOUT_KEY);
            } catch {
                success = false;
            }

            if (!success) {
                deps.batch(() => {
                    deps.setSelected(previousMethod, previousPrices);
                });
            } else {
                await deps.identify();
            }

            const queued = deps.flight.queued;
            deps.flight.queued = undefined;
            deps.batch(() => {
                deps.setQueued(undefined);
            });
            if (queued == undefined || isSameMethod(deps.getSelected(), queued) || isSameMethod(nextOption, queued)) {
                break;
            }
            nextOption = queued;
        }
    } finally {
        deps.flight.pending = false;
        deps.flight.queued = undefined;
        deps.flight.inFlight = undefined;
        deps.batch(() => {
            deps.setBusy(false);
            deps.setQueued(undefined);
        });
    }
}
